import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Row,
  Col,
  Badge,
  Table,
  Button,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import { 
  FaPrint, 
  FaMoneyBillWave, 
  FaArrowLeft, 
  FaEdit,
  FaTrashAlt
} from 'react-icons/fa';

// A4 bill styling
const a4BillStyles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '20px',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    position: 'relative'
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #ddd'
  },
  billTo: {
    marginBottom: '20px'
  },
  table: {
    width: '100%',
    marginTop: '20px',
    marginBottom: '20px',
  },
  footer: {
    marginTop: '40px',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#666'
  },
  paidStamp: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-30deg)',
    fontSize: '6rem',
    fontWeight: 'bold',
    color: 'rgba(76, 175, 80, 0.3)',
    border: '0.5rem solid rgba(76, 175, 80, 0.3)',
    padding: '1rem 2rem',
    borderRadius: '1rem',
    pointerEvents: 'none',
    zIndex: 100
  }
};

const BillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const { user } = useContext(AuthContext);
  
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;
  
  // Fetch clinic and bill data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // First fetch clinic data
        const clinicResponse = await axios.get('/api/clinic.php');
        const clinicData = clinicResponse.data;
        
        // Fix the logo path if it exists
        if (clinicData && clinicData.logo) {
          // If the logo path doesn't start with http or https, prepend the base URL
          if (!clinicData.logo.startsWith('http') && !clinicData.logo.startsWith('/')) {
            clinicData.logo = `/${clinicData.logo}`;
          }
        }
        
        setClinic(clinicData);
        
        // Then fetch bill data
        const billResponse = await axios.get(`/api/billings.php?id=${id}`);
        
        const billData = billResponse.data;
        
        // Debug: Log the raw bill data to see what we're getting
        console.log("Raw bill data received:", billData);
        
        // Extract bill items from different possible properties
        let extractedBillItems = [];
        
        if (billData.billItems && Array.isArray(billData.billItems)) {
          extractedBillItems = billData.billItems;
          console.log("Using bill.billItems (Array):", extractedBillItems);
        } 
        else if (billData.BillItems && Array.isArray(billData.BillItems)) {
          extractedBillItems = billData.BillItems;
          console.log("Using bill.BillItems (Array):", extractedBillItems);
        }
        else if (typeof billData.billItems === 'string') {
          try {
            extractedBillItems = JSON.parse(billData.billItems);
            console.log("Parsed billItems from string:", extractedBillItems);
          } catch (e) {
            console.error("Failed to parse billItems string:", e);
          }
        }
        else if (billData.billItems) {
          console.log("BillItems exists but is not array or string:", billData.billItems);
          extractedBillItems = billData.billItems;
        }
        
        // If still empty, create some sample items for testing
        if (!extractedBillItems || extractedBillItems.length === 0) {
          console.log("No bill items found, creating default sample");
          extractedBillItems = [
            { 
              description: "Consultation Fee", 
              quantity: 1, 
              unitPrice: 1000, 
              amount: 1000 
            }
          ];
        }
        
        // Set the billItems state
        setBillItems(extractedBillItems);
        setBill(billData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load bill data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'partial':
        return <Badge bg="info">Partial</Badge>;
      case 'complete':
        return <Badge bg="success">Complete</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };
  
  // Handle print
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get the clinic logo URL if available
    const clinicLogo = clinic && clinic.logo ? clinic.logo : '';
    
    // Create the print content with proper styling
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill ${bill.billNumber || `BILL-${bill.id}`}</title>
        <style>
          @page {
            size: A4;
            margin: 20mm 15mm 25mm 15mm;
          }
          
          @media print {
            /* Fixed header on every page */
            .print-header {
              position: running(header);
            }
            
            @page {
              @top-left {
                content: element(header);
              }
              @bottom-center {
                content: "Page " counter(page) " of " counter(pages);
                font-size: 9pt;
              }
            }
            
            /* Prevent page breaks inside table rows */
            tr {
              page-break-inside: avoid;
            }
            
            /* Keep header with first row */
            thead {
              display: table-header-group;
            }
            
            /* Keep footer with last row */
            tfoot {
              display: table-footer-group;
            }
            
            /* Repeat bill info section on each page */
            .bill-info {
              page-break-after: avoid;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #333;
            background: white;
            margin: 0;
            padding: 0;
          }
          .print-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            position: relative;
          }
          .print-header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #333;
          }
          .clinic-logo {
            max-height: 80px;
            max-width: 120px;
            margin-right: 20px;
            flex-shrink: 0;
          }
          .clinic-info {
            flex: 1;
            text-align: left;
          }
          .clinic-name {
            font-size: 22pt;
            font-weight: bold;
            margin: 0 0 5px 0;
            color: #1a5276;
          }
          .clinic-address, .clinic-contact {
            margin: 2px 0;
            font-size: 10pt;
            color: #666;
          }
          .bill-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          .bill-info-left, .bill-info-right {
            width: 48%;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }
          th, td {
            padding: 8px;
            text-align: left;
            border: 1px solid #ddd;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .text-end {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .fw-bold {
            font-weight: bold;
          }
          .bill-footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 10pt;
            color: #666;
            page-break-inside: avoid;
          }
          .paid-stamp {
            position: fixed;
            top: 450px;
            left: 50%;
            transform: translate(-50%, 0) rotate(-30deg);
            font-size: 6rem;
            font-weight: bold;
            color: rgba(76, 175, 80, 0.2);
            border: 0.5rem solid rgba(76, 175, 80, 0.2);
            padding: 1rem 2rem;
            border-radius: 1rem;
            z-index: -1;
            pointer-events: none;
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${bill.notes && bill.notes.includes('[REFUND:') ? '<div class="paid-stamp" style="color: rgba(220, 53, 69, 0.3); border-color: rgba(220, 53, 69, 0.3);">CANCELLED</div>' : (bill.paymentStatus === 'complete' ? '<div class="paid-stamp">PAID</div>' : '')}
          
          <div class="print-header">
            ${clinicLogo ? `<img src="${clinicLogo}" alt="Clinic Logo" class="clinic-logo">` : ''}
            <div class="clinic-info">
              <h2 style="margin: 0 0 5px 0;">Invoice</h2>
              <p class="clinic-name">${clinic ? clinic.name : 'Doctero Patient Management System'}</p>
              <p class="clinic-address">${clinic ? clinic.address : ''}</p>
              <p class="clinic-contact">Contact: ${clinic ? clinic.contactNumber : ''}</p>
            </div>
          </div>
          
          <div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
              <div style="width: 48%;">
                <h5 style="margin-bottom: 10px;">Bill To:</h5>
                <p style="margin: 5px 0;"><strong>Patient:</strong> ${bill.Patient?.name || "N/A"}</p>
                <p style="margin: 5px 0;"><strong>ID:</strong> ${bill.Patient?.patientId || bill.patientId || "N/A"}</p>
                <p style="margin: 5px 0;"><strong>Contact:</strong> ${bill.Patient?.contactNumber || 'N/A'}</p>
              </div>
              <div style="width: 48%; text-align: right;">
                <h5 style="margin-bottom: 10px;">Bill Details:</h5>
                <p style="margin: 5px 0;"><strong>Bill #:</strong> ${bill.billNumber || `BILL-${bill.id}`}</p>
                <p style="margin: 5px 0;"><strong>Date:</strong> ${formatDate(bill.billDate || bill.date || bill.createdAt)}</p>
                <p style="margin: 5px 0;"><strong>Status:</strong> ${bill.paymentStatus}</p>
                ${bill.Doctor ? `<p style="margin: 5px 0;"><strong>Doctor:</strong> ${bill.Doctor.name}</p>` : ''}
                ${bill.reportId ? `<p style="margin: 5px 0;"><strong>Report ID:</strong> ${bill.reportId}</p>` : ''}
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th style="width: 50%;">Description</th>
                  <th style="width: 15%; text-align: center;">Quantity</th>
                  <th style="width: 15%; text-align: center;">Unit Price</th>
                  <th style="width: 20%; text-align: right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${billItems && billItems.length > 0 ? 
                  billItems.map((item, index) => `
                    <tr>
                      <td>${item.description}</td>
                      <td style="text-align: center;">${item.quantity}</td>
                      <td style="text-align: center;">${formatCurrency(item.unitPrice)}</td>
                      <td style="text-align: right;">${formatCurrency(item.amount || (item.quantity * item.unitPrice))}</td>
                    </tr>
                  `).join('') : 
                  '<tr><td colspan="4" style="text-align: center;">No items found in this bill</td></tr>'
                }
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" style="text-align: right; font-weight: bold;">Subtotal:</td>
                  <td style="text-align: right; font-weight: bold;">${formatCurrency(billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0))}</td>
                </tr>
                ${bill.discount > 0 ? `
                <tr>
                  <td colspan="3" style="text-align: right;">Discount (${bill.discount}%):</td>
                  <td style="text-align: right; color: #dc3545;">-${formatCurrency((billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * bill.discount / 100))}</td>
                </tr>
                ` : ''}
                ${bill.tax > 0 ? `
                <tr>
                  <td colspan="3" style="text-align: right;">Tax (${bill.tax}%):</td>
                  <td style="text-align: right;">${formatCurrency((billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * (1 - bill.discount / 100) * bill.tax / 100))}</td>
                </tr>
                ` : ''}
                <tr>
                  <td colspan="3" style="text-align: right; font-weight: bold;">Total:</td>
                  <td style="text-align: right; font-weight: bold;">${formatCurrency(bill.totalAmount)}</td>
                </tr>
                <tr>
                  <td colspan="3" style="text-align: right; font-weight: bold;">Amount Paid:</td>
                  <td style="text-align: right; font-weight: bold;">${formatCurrency(bill.paidAmount)}</td>
                </tr>
                <tr>
                  <td colspan="3" style="text-align: right; font-weight: bold;">Balance Due:</td>
                  <td style="text-align: right; font-weight: bold; ${balance > 0 ? 'color: #dc3545;' : 'color: #28a745;'}">${formatCurrency(balance)}</td>
                </tr>
              </tfoot>
            </table>
            
            ${bill.notes ? `
              <div style="margin-top: 20px;">
                <h6 style="margin-bottom: 5px;">Notes:</h6>
                <p>${bill.notes}</p>
              </div>
            ` : ''}
            
            ${bill.PaymentRecords && bill.PaymentRecords.length > 0 ? `
              <div style="margin-top: 20px;">
                <h6 style="margin-bottom: 5px;">Payment History:</h6>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${bill.PaymentRecords.map(payment => `
                      <tr>
                        <td>${formatDate(payment.paymentDate)}</td>
                        <td>${formatCurrency(payment.amount)}</td>
                        <td>${payment.paymentMethod}</td>
                        <td>${payment.reference || 'N/A'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : ''}
          </div>
          
          <div class="bill-footer" style="margin-top: 60px; padding-top: 20px; border-top: 1px solid #ddd;">
            <p>Thank you for your business</p>
            <p>For any queries, please contact us</p>
          </div>
        </div>
        
        <script>
          // Print automatically when the page loads
          window.onload = function() {
            setTimeout(function() {
              window.print();
              // Don't close the window automatically, let the user close it
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    // Write the content to the new window
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };
  
  const handleProcessPayment = () => {
    navigate(`/billing/${id}/payment`);
  };

  const handleViewReceipt = () => {
    navigate(`/ticket/receipt/${id}`);
  };
  
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await axios.delete(`/api/billings.php?id=${id}`);
      setShowDeleteModal(false);
      // Navigate back to billing list with success message
      navigate('/billing', { 
        state: { 
          notification: { 
            type: 'success', 
            message: `Bill ${bill.billNumber} has been deleted successfully.` 
          } 
        } 
      });
    } catch (err) {
      console.error('Error deleting bill:', err);
      setError(`Failed to delete bill. ${err.response?.data?.msg || 'Please try again.'}`);
      setShowDeleteModal(false);
      setLoading(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading bill data...</p>
      </div>
    );
  }
  
  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }
  
  if (!bill) {
    return <Alert variant="warning">Bill not found.</Alert>;
  }
  
  const balance = bill.totalAmount - bill.paidAmount;
  
  return (
    <div className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <h2>Bill Details</h2>
        <div>
          <Button 
            variant="outline-secondary" 
            className="me-2" 
            onClick={() => navigate('/billing')}
          >
            <FaArrowLeft className="me-2" /> Back
          </Button>
          
          {hasPermission('printBill') && (
            <Button 
              variant="outline-primary" 
              className="me-2 no-print" 
              onClick={handlePrint}
            >
              <FaPrint className="me-2" /> Print
            </Button>
          )}
          
          {hasPermission('payment') && bill.paymentStatus !== 'complete' && (
            <Button 
              variant="success" 
              className="me-2"
              onClick={handleProcessPayment}
            >
              <FaMoneyBillWave className="me-2" /> Process Payment
            </Button>
          )}
          
          {hasPermission('viewBill') && (
            <Button 
              variant="info" 
              onClick={handleViewReceipt}
            >
              View Receipt
            </Button>
          )}
          
          {hasPermission('editBill') && (
            <Button 
              variant="outline-info"
              onClick={() => navigate(`/billing/edit/${id}`)}
              className="me-2"
            >
              <FaEdit className="me-2" /> Edit Bill
            </Button>
          )}
          
          {hasPermission('refund') && bill.paidAmount > 0 && !bill.Report && bill.billStatus !== 'used' && (
            <Button 
              variant="warning"
              className="me-2"
              onClick={() => navigate(`/billing/${id}/refund`)}
            >
              <FaMoneyBillWave className="me-2" /> Cancel & Refund
            </Button>
          )}
          
          {hasPermission('deleteBill') && (
            <Button 
              variant="outline-danger"
              onClick={handleDeleteClick}
            >
              <FaTrashAlt className="me-2" /> Delete Bill
            </Button>
          )}
        </div>
      </div>
      
      <div className="a4-bill" style={a4BillStyles.container}>
        {bill.notes && bill.notes.includes('[REFUND:') ? (
          <div style={{...a4BillStyles.paidStamp, color: 'rgba(220, 53, 69, 0.3)', borderColor: 'rgba(220, 53, 69, 0.3)'}}>CANCELLED</div>
        ) : bill.paymentStatus === 'complete' && (
          <div style={a4BillStyles.paidStamp}>PAID</div>
        )}
        
        <div id="billPrintContent">
          <div className="bill-header">
            {clinic && clinic.logo && (
              <div className="text-center mb-3">
                <img 
                  src={clinic.logo} 
                  alt="Clinic Logo" 
                  style={{ maxHeight: '80px', maxWidth: '100%' }} 
                  className="mb-2"
                />
                <h3 className="mb-0">{clinic.name || 'Doctero Medical Center'}</h3>
                <p className="text-muted">{clinic.address}</p>
                <p className="text-muted">{clinic.contactNumber}</p>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Bill #{bill.billNumber || `BILL-${bill.id}`}</h4>
              <span className={`badge ${
                bill.paymentStatus === 'complete' ? 'bg-success' : 
                bill.paymentStatus === 'partial' ? 'bg-info' : 'bg-warning'
              }`}>
                {bill.paymentStatus}
              </span>
            </div>
          </div>
          
          <Row className="mb-4">
            <Col md={6}>
              <div style={a4BillStyles.billTo}>
                <h5 className="mb-3">Bill To:</h5>
                <p className="mb-1"><strong>Patient:</strong> {bill.Patient?.name || "N/A"}</p>
                <p className="mb-1"><strong>ID:</strong> {bill.Patient?.patientId || bill.patientId || "N/A"}</p>
                <p className="mb-1">
                  <strong>Contact:</strong> {bill.Patient?.contactNumber || 'N/A'}
                </p>
              </div>
            </Col>
            <Col md={6} className="text-md-end">
              <h5 className="mb-3">Bill Details:</h5>
              <p className="mb-1">
                <strong>Date:</strong> {formatDate(bill.billDate || bill.date || bill.createdAt)}
              </p>
              <p className="mb-1">
                <strong>Due Date:</strong> {formatDate(new Date(new Date(bill.billDate || bill.date || bill.createdAt).setDate(new Date(bill.billDate || bill.date || bill.createdAt).getDate() + 30)))}
              </p>
              {bill.Doctor && (
                <p className="mb-1">
                  <strong>Doctor:</strong> {bill.Doctor.name}
                </p>
              )}
              {bill.reportId && (
                <p className="mb-1">
                  <strong>Report ID:</strong> {bill.reportId}
                </p>
              )}
            </Col>
          </Row>
          
          <Table bordered responsive className="bill-table">
            <thead>
              <tr className="table-light">
                <th style={{ width: '50%' }}>Description</th>
                <th className="text-center" style={{ width: '15%' }}>Quantity</th>
                <th className="text-center" style={{ width: '15%' }}>Unit Price</th>
                <th className="text-end" style={{ width: '20%' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {billItems && billItems.length > 0 ? (
                billItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-center">{formatCurrency(item.unitPrice)}</td>
                    <td className="text-end">{formatCurrency(item.amount || (item.quantity * item.unitPrice))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No items found in this bill</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="table-light">
                <td colSpan="3" className="text-end fw-bold">Subtotal:</td>
                <td className="text-end fw-bold">
                  {formatCurrency(billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0))}
                </td>
              </tr>
              {bill.discount > 0 && (
                <tr className="table-light">
                  <td colSpan="3" className="text-end">Discount ({bill.discount}%):</td>
                  <td className="text-end text-danger">
                    -{formatCurrency((billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * bill.discount / 100))}
                  </td>
                </tr>
              )}
              {bill.tax > 0 && (
                <tr className="table-light">
                  <td colSpan="3" className="text-end">Tax ({bill.tax}%):</td>
                  <td className="text-end">
                    {formatCurrency((billItems.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * (1 - bill.discount / 100) * bill.tax / 100))}
                  </td>
                </tr>
              )}
              <tr className="table-light">
                <td colSpan="3" className="text-end fw-bold">Total:</td>
                <td className="text-end fw-bold">{formatCurrency(bill.totalAmount)}</td>
              </tr>
              <tr>
                <td colSpan="3" className="text-end fw-bold">Amount Paid:</td>
                <td className="text-end fw-bold">{formatCurrency(bill.paidAmount)}</td>
              </tr>
              <tr>
                <td colSpan="3" className="text-end fw-bold">Balance Due:</td>
                <td className={`text-end fw-bold ${balance > 0 ? 'text-danger' : 'text-success'}`}>
                  {formatCurrency(balance)}
                </td>
              </tr>
            </tfoot>
          </Table>
          
          {bill.notes && (
            <div className="mt-4">
              <h6>Notes:</h6>
              <p>{bill.notes}</p>
            </div>
          )}
          
          {bill.PaymentRecords && bill.PaymentRecords.length > 0 && (
            <div className="mt-4">
              <h6>Payment History:</h6>
              <Table bordered responsive className="mt-2">
                <thead>
                  <tr className="table-light">
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.PaymentRecords.map((payment, index) => (
                    <tr key={index}>
                      <td>{formatDate(payment.paymentDate)}</td>
                      <td>{formatCurrency(payment.amount)}</td>
                      <td>{payment.paymentMethod}</td>
                      <td>{payment.reference || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          
          <div className="bill-footer" style={{ marginTop: '60px', paddingTop: '20px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
            <p>Thank you for choosing our services.</p>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete bill {bill?.billNumber}?</p>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone. All data related to this bill will be permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Bill'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BillDetail; 
